import { useState, useCallback, useRef } from "react";
import { LayoutItem } from "@/components/admin/DashboardWidgetConfig";

interface HistoryState {
  layout: LayoutItem[];
  visibleWidgets: string[];
}

interface UseLayoutHistoryReturn {
  pushState: (state: HistoryState) => void;
  undo: () => HistoryState | null;
  redo: () => HistoryState | null;
  canUndo: boolean;
  canRedo: boolean;
  clearHistory: () => void;
}

const MAX_HISTORY_SIZE = 50;

export function useLayoutHistory(initialState?: HistoryState): UseLayoutHistoryReturn {
  const [history, setHistory] = useState<HistoryState[]>(
    initialState ? [initialState] : []
  );
  const [currentIndex, setCurrentIndex] = useState(initialState ? 0 : -1);
  const isUndoRedoRef = useRef(false);

  const pushState = useCallback((state: HistoryState) => {
    // Skip if this is triggered by undo/redo
    if (isUndoRedoRef.current) {
      isUndoRedoRef.current = false;
      return;
    }

    setHistory(prev => {
      // Remove any future states if we're not at the end
      const newHistory = prev.slice(0, currentIndex + 1);
      
      // Check if the new state is different from the current state
      const lastState = newHistory[newHistory.length - 1];
      if (lastState && 
          JSON.stringify(lastState.layout) === JSON.stringify(state.layout) &&
          JSON.stringify(lastState.visibleWidgets) === JSON.stringify(state.visibleWidgets)) {
        return prev; // No change needed
      }
      
      // Add new state
      newHistory.push({
        layout: JSON.parse(JSON.stringify(state.layout)),
        visibleWidgets: [...state.visibleWidgets],
      });
      
      // Limit history size
      if (newHistory.length > MAX_HISTORY_SIZE) {
        newHistory.shift();
      }
      
      return newHistory;
    });
    
    setCurrentIndex(prev => {
      const newIndex = Math.min(prev + 1, MAX_HISTORY_SIZE - 1);
      return newIndex;
    });
  }, [currentIndex]);

  const undo = useCallback((): HistoryState | null => {
    if (currentIndex <= 0) return null;
    
    isUndoRedoRef.current = true;
    const newIndex = currentIndex - 1;
    setCurrentIndex(newIndex);
    
    const state = history[newIndex];
    return state ? {
      layout: JSON.parse(JSON.stringify(state.layout)),
      visibleWidgets: [...state.visibleWidgets],
    } : null;
  }, [currentIndex, history]);

  const redo = useCallback((): HistoryState | null => {
    if (currentIndex >= history.length - 1) return null;
    
    isUndoRedoRef.current = true;
    const newIndex = currentIndex + 1;
    setCurrentIndex(newIndex);
    
    const state = history[newIndex];
    return state ? {
      layout: JSON.parse(JSON.stringify(state.layout)),
      visibleWidgets: [...state.visibleWidgets],
    } : null;
  }, [currentIndex, history]);

  const clearHistory = useCallback(() => {
    setHistory([]);
    setCurrentIndex(-1);
  }, []);

  return {
    pushState,
    undo,
    redo,
    canUndo: currentIndex > 0,
    canRedo: currentIndex < history.length - 1,
    clearHistory,
  };
}
